import React, { useState, useRef } from "react";
import { lang } from "../../../middleware/lang";

interface CsvImportModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface ImportResult {
  created: number;
  updated: number;
  stock_adjusted: number;
  errors: { row: number; message: string }[];
}

const getApiBaseUrl = (): string => {
  const graphUrl = process.env.REACT_APP_GRAPH_URL || "";
  // Strip the path (e.g. "/pos") to get the base origin
  try {
    const url = new URL(graphUrl);
    return url.origin;
  } catch {
    return "";
  }
};

const parseCsvPreview = (
  text: string
): { headers: string[]; rows: string[][] } => {
  const lines = text.split(/\r?\n/).filter((line) => line.trim() !== "");
  if (lines.length === 0) return { headers: [], rows: [] };

  const parseLine = (line: string): string[] => {
    const fields: string[] = [];
    let current = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQuotes) {
        if (ch === '"') {
          if (i + 1 < line.length && line[i + 1] === '"') {
            current += '"';
            i++;
          } else {
            inQuotes = false;
          }
        } else {
          current += ch;
        }
      } else {
        if (ch === '"') {
          inQuotes = true;
        } else if (ch === ",") {
          fields.push(current);
          current = "";
        } else {
          current += ch;
        }
      }
    }
    fields.push(current);
    return fields;
  };

  // Strip BOM if present
  const clean = lines[0].replace(/^\uFEFF/, "");
  const headers = parseLine(clean);
  const rows = lines.slice(1, 6).map(parseLine); // first 5 data rows
  return { headers, rows };
};

const CsvImportModal: React.FC<CsvImportModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<{
    headers: string[];
    rows: string[][];
    totalRows: number;
  } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setError(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setResult(null);
    setError(null);
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) {
      setFile(null);
      setPreview(null);
      return;
    }
    setFile(selectedFile);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const { headers, rows } = parseCsvPreview(text);
      const allLines = text.split(/\r?\n/).filter((l) => l.trim() !== "");
      setPreview({ headers, rows, totalRows: Math.max(0, allLines.length - 1) });
    };
    reader.readAsText(selectedFile, "UTF-8");
  };

  const handleImport = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const baseUrl = getApiBaseUrl();
      const token = localStorage.getItem("token");
      const res = await fetch(`${baseUrl}/inventory/csv-import`, {
        method: "POST",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: formData,
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(
          errData?.message || `Error ${res.status}: ${res.statusText}`
        );
      }

      const data: ImportResult = await res.json();
      setResult(data);

      if (data.created > 0 || data.updated > 0 || data.stock_adjusted > 0) {
        onSuccess();
      }
    } catch (err: any) {
      setError(err.message || lang("Error al importar", "Import error"));
    } finally {
      setUploading(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <div
        className="modal fade show d-block"
        tabIndex={-1}
        role="dialog"
        style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      >
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fa fa-upload mr-2" />
                {lang("Importar Inventario CSV", "Import Inventory CSV")}
              </h5>
              <button
                type="button"
                className="close"
                onClick={handleClose}
                aria-label="Close"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="modal-body">
              {/* File input */}
              <div className="form-group">
                <label>
                  {lang(
                    "Seleccionar archivo CSV",
                    "Select CSV file"
                  )}
                </label>
                <input
                  ref={fileRef}
                  type="file"
                  className="form-control-file"
                  accept=".csv"
                  onChange={handleFileChange}
                />
                <small className="form-text text-muted">
                  {lang(
                    "Descarga la plantilla CSV primero para ver el formato correcto.",
                    "Download the CSV template first to see the correct format."
                  )}
                </small>
              </div>

              {/* Preview */}
              {preview && (
                <>
                  <p className="text-muted mb-2">
                    <strong>{preview.totalRows}</strong>{" "}
                    {lang("filas encontradas", "rows found")}
                    {preview.rows.length < preview.totalRows &&
                      ` (${lang(
                        `mostrando primeras ${preview.rows.length}`,
                        `showing first ${preview.rows.length}`
                      )})`}
                  </p>
                  <div className="table-responsive mb-3" style={{ maxHeight: 250, overflow: "auto" }}>
                    <table className="table table-sm table-bordered">
                      <thead className="thead-light">
                        <tr>
                          {preview.headers.map((h, i) => (
                            <th key={i} style={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {preview.rows.map((row, ri) => (
                          <tr key={ri}>
                            {preview.headers.map((_, ci) => (
                              <td key={ci} style={{ fontSize: "0.8rem" }}>
                                {row[ci] || ""}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {/* Error */}
              {error && (
                <div className="alert alert-danger">
                  <i className="fa fa-exclamation-circle mr-1" />
                  {error}
                </div>
              )}

              {/* Result */}
              {result && (
                <div className="alert alert-success">
                  <h6 className="alert-heading">
                    {lang("Importacion completada", "Import completed")}
                  </h6>
                  <ul className="mb-0">
                    {result.created > 0 && (
                      <li>
                        <strong>{result.created}</strong>{" "}
                        {lang("ingredientes creados", "ingredients created")}
                      </li>
                    )}
                    {result.updated > 0 && (
                      <li>
                        <strong>{result.updated}</strong>{" "}
                        {lang("ingredientes actualizados", "ingredients updated")}
                      </li>
                    )}
                    {result.stock_adjusted > 0 && (
                      <li>
                        <strong>{result.stock_adjusted}</strong>{" "}
                        {lang(
                          "niveles de stock ajustados",
                          "stock levels adjusted"
                        )}
                      </li>
                    )}
                    {result.created === 0 &&
                      result.updated === 0 &&
                      result.stock_adjusted === 0 && (
                        <li>{lang("Sin cambios", "No changes")}</li>
                      )}
                  </ul>
                  {result.errors && result.errors.length > 0 && (
                    <div className="mt-2">
                      <strong className="text-danger">
                        {lang("Errores:", "Errors:")}
                      </strong>
                      <ul className="mb-0 text-danger">
                        {result.errors.map((err, i) => (
                          <li key={i}>
                            {lang("Fila", "Row")} {err.row}: {err.message}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleClose}
              >
                {lang("Cerrar", "Close")}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleImport}
                disabled={!file || uploading}
              >
                {uploading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm mr-1"
                      role="status"
                      aria-hidden="true"
                    />
                    {lang("Importando...", "Importing...")}
                  </>
                ) : (
                  <>
                    <i className="fa fa-upload mr-1" />
                    {lang("Importar", "Import")}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CsvImportModal;
